/**
 * DecisionRightsPanel
 *
 * Shows the decision rights matrix for a cycle: for each decision the cycle
 * must make (domain × class), who holds the right, what modes they may use
 * (solo / delegate / session), and who may reassign the right.
 *
 * Data source: decisionRightsStore (DB-backed, §2.4 compliant).
 * Falls back to computed defaults when no rights are persisted yet, with a
 * visible "not yet configured" notice and a "Use defaults" CTA.
 *
 * Surfaces in: CycleWorkspace → Participation tab (ScopeTab)
 */

import { useEffect, useState, useMemo } from 'react';
import {
  User,
  Users,
  ArrowRight,
  Lock,
  Info,
  ShieldCheck,
  ChevronRight,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  X as XIcon,
} from 'lucide-react';
import {
  type DecisionRight,
  type DecisionExecutionMode,
  type DelegatedDecisionRight,
  createDefaultDecisionRights,
  DECISION_CLASS_EXECUTION_CONSTRAINTS,
  DECISION_EXECUTION_MODE_LABELS,
} from '@/types/prioritization/decisionRights';
import type { CanonicalSessionType } from '@/types/prioritization/session';
import {
  CANONICAL_SESSION_TYPE_LABELS,
  CANONICAL_DECISION_CLASS_LABELS,
  CANONICAL_DECISION_CLASS_DESCRIPTIONS,
} from '@/types/prioritization/session';
import { useDecisionRightsStore } from '@/stores/prioritization/decisionRightsStore';
import { useUIStore } from '@/stores/prioritization/uiStore';
import { useUserStore } from '@/stores/prioritization/userStore';
import { useCycleParticipantsStore } from '@/stores/prioritization/cycleParticipantsStore';
import { useSystemUsers } from '@/hooks/useSystemUsers';
import { Modal, Select, Field, Checkbox, Callout } from '@/components/ds';
import { endOfDayIso } from '@/lib/decisionRightDelegations';

// ============================================================================
// Types
// ============================================================================

interface DecisionRightsPanelProps {
  cycleId: string;
  cycleOwnerId?: string;
  cycleOwnerName?: string;
  /** Whether the current user may edit rights (has decision_rights.assign). */
  canEdit?: boolean;
}

// ============================================================================
// Mode chip visual config
// ============================================================================

const MODE_CONFIG: Record<
  DecisionExecutionMode,
  { icon: React.ElementType; color: string; bgColor: string; borderColor: string }
> = {
  solo:     { icon: User,       color: 'text-[var(--text-sec)]',  bgColor: 'bg-[var(--hover-tint)]',  borderColor: 'border-[var(--rule)]' },
  delegate: { icon: ArrowRight, color: 'text-[var(--text-sec)]', bgColor: 'bg-[var(--slate-dim)]',  borderColor: 'border-[var(--slate)]' },
  session:  { icon: Users,      color: 'text-[var(--green)]',   bgColor: 'bg-[var(--green-dim)]',    borderColor: 'border-[var(--green)]' },
};

const DOMAIN_ORDER: CanonicalSessionType[] = [
  'participants',
  'item_pool',
  'criteria',
  'scales',
  'values',
  'items',
];

const DOMAIN_DESCRIPTIONS: Record<CanonicalSessionType, string> = {
  participants: 'Who is in scope for this cycle',
  item_pool:    'Which items are candidates for ranking',
  criteria:     'Which attributes become evaluation criteria',
  scales:       'Value ranges and scales for each attribute',
  values:       'Attribute values assigned to items',
  items:        'Final priority ordering',
};

// ============================================================================
// Component
// ============================================================================

export function DecisionRightsPanel({
  cycleId,
  cycleOwnerId,
  cycleOwnerName,
  canEdit = false,
}: DecisionRightsPanelProps) {
  const userId = useUserStore(state => state.userId);

  const loadRights        = useDecisionRightsStore(s => s.loadRights);
  const getConfig         = useDecisionRightsStore(s => s.getConfigForCycle);
  const isLoadingStore    = useDecisionRightsStore(s => s.isLoading);
  const getError          = useDecisionRightsStore(s => s.getError);
  const initDefaults      = useDecisionRightsStore(s => s.initializeDefaults);
  const delegateRight     = useDecisionRightsStore(s => s.delegateRight);
  const revokeDelegation  = useDecisionRightsStore(s => s.revokeDelegation);
  const delegationsByCycle = useDecisionRightsStore(s => s.delegationsByCycle);

  const [initializing, setInitializing] = useState(false);
  const [initError, setInitError]       = useState<string | null>(null);
  const [initSuccess, setInitSuccess]   = useState(false);

  // The right the chevron's delegate/revoke panel is currently open for.
  const [managingRightId, setManagingRightId] = useState<string | null>(null);

  // Cycle participants — the pool a right can be delegated to.
  const allParticipants        = useCycleParticipantsStore(s => s.participants);
  const fetchParticipants      = useCycleParticipantsStore(s => s.fetchParticipantsForCycle);
  const { users: systemUsers } = useSystemUsers();

  // Load on mount and when cycleId changes (§2.4 — read path)
  useEffect(() => {
    loadRights(cycleId);
    fetchParticipants(cycleId);
  }, [cycleId, loadRights, fetchParticipants]);

  const activeParticipants = useMemo(
    () => allParticipants.filter(p => p.cycleId === cycleId && p.status === 'active'),
    [allParticipants, cycleId]
  );
  const participantOptions = useMemo(
    () =>
      activeParticipants.map(p => {
        const su = systemUsers.find(u => u.id === p.userId);
        return { value: p.userId, label: su?.name ?? su?.email ?? p.userId };
      }),
    [activeParticipants, systemUsers]
  );

  const dbConfig    = getConfig(cycleId);
  const loading     = isLoadingStore(cycleId);
  const loadError   = getError(cycleId);
  const hasDbRights = dbConfig !== null && dbConfig.rights.length > 0;

  // Computed fallback shown when DB has no rows yet
  const fallbackConfig = useMemo(
    () => createDefaultDecisionRights(cycleId, cycleOwnerId ?? 'cycle-owner', cycleOwnerId ?? 'system'),
    [cycleId, cycleOwnerId]
  );

  const effectiveConfig = hasDbRights ? dbConfig! : fallbackConfig;

  const handleInitDefaults = async () => {
    if (!cycleOwnerId) return;
    setInitializing(true);
    setInitError(null);
    setInitSuccess(false);
    const result = await initDefaults(cycleId, cycleOwnerId, userId ?? cycleOwnerId);
    setInitializing(false);
    if (result.success) {
      setInitSuccess(true);
    } else {
      setInitError(result.error ?? 'Failed to initialize defaults');
    }
  };

  // ── Loading state ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 text-[var(--text-ter)] text-sm">
        <RefreshCw className="h-4 w-4 animate-spin" />
        Loading decision rights…
      </div>
    );
  }

  // ── Load error ────────────────────────────────────────────────────────────
  if (loadError) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
        <AlertCircle className="h-4 w-4 shrink-0 text-[var(--coral)]" />
        Failed to load decision rights: {loadError}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Section header. There used to be a header-level "Configure" button
          here too, but it had no destination distinct from the per-row
          chevron below (which opens that row's own delegate/revoke panel) —
          two dead-end affordances for the same job is worse than one. */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-base font-semibold text-[var(--text-pri)] flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-[var(--text-ter)]" />
            Decision Rights
          </h3>
          <p className="mt-1 text-sm text-[var(--text-ter)] max-w-2xl">
            For each decision this cycle must make, this matrix shows who holds
            the right, which execution modes they may use, and who may reassign
            the right.
          </p>
        </div>
      </div>

      {/* Init success banner */}
      {initSuccess && (
        <div className="flex items-center gap-2 px-4 py-3 bg-[var(--green-dim)] border border-[var(--green)] text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
          <CheckCircle className="h-4 w-4 shrink-0 text-[var(--green)]" />
          Default decision rights saved. This configuration now survives refresh.
        </div>
      )}

      {/* Init error banner */}
      {initError && (
        <div className="flex items-center gap-2 px-4 py-3 bg-[var(--coral-dim)] border border-[var(--coral)] text-sm text-[var(--coral)]">
          <AlertCircle className="h-4 w-4 shrink-0 text-[var(--coral)]" />
          {initError}
        </div>
      )}

      {/* Not-yet-configured notice (DB has no rows) */}
      {!hasDbRights && !initSuccess && (
        <div className="flex items-start gap-3 px-4 py-3 bg-[var(--mustard-dim)] border border-[var(--mustard)] text-sm text-[var(--mustard)]">
          <Info className="h-4 w-4 shrink-0 mt-0.5 text-[var(--mustard)]" />
          <div className="flex-1">
            <span>
              Decision rights have not been explicitly configured for this cycle.
              The preview below shows the defaults that will apply: the Cycle Owner
              holds all rights with all permitted modes. Delegation is not enabled.
            </span>
            {cycleOwnerId && (
              <div className="mt-2">
                <button
                  onClick={handleInitDefaults}
                  disabled={initializing}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-[var(--mustard)] bg-[var(--mustard-dim)] hover:bg-[var(--mustard-dim)] border border-[var(--mustard)] transition-colors disabled:opacity-50"
                >
                  {initializing
                    ? <><RefreshCw className="h-3 w-3 animate-spin" /> Saving…</>
                    : 'Save defaults to database'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rights matrix */}
      <div className="space-y-3">
        {DOMAIN_ORDER.map(domain => {
          const rights = effectiveConfig.rights.filter(r => r.domain === domain);
          return (
            <DomainSection
              key={domain}
              domain={domain}
              rights={rights}
              cycleOwnerName={cycleOwnerName}
              canEdit={canEdit}
              // Delegation targets real decision_rights rows — the fallback
              // preview's rights are computed, not persisted, so there is
              // nothing yet for delegate_decision_right to point at.
              onManage={hasDbRights ? (right) => setManagingRightId(right.id) : undefined}
            />
          );
        })}
      </div>

      {/* Legend */}
      <ExecutionModeLegend />

      {/* Delegate / revoke panel for the row whose chevron was clicked */}
      {managingRightId && (() => {
        const managingRight = effectiveConfig.rights.find(r => r.id === managingRightId);
        if (!managingRight) return null;
        const delegations = (delegationsByCycle[cycleId] ?? []).filter(
          d => d.sourceRightId === managingRightId && !d.revoked
        );
        return (
          <RightManagePanel
            right={managingRight}
            cycleOwnerName={cycleOwnerName}
            participantOptions={participantOptions}
            delegations={delegations}
            callerId={userId ?? cycleOwnerId ?? ''}
            delegateRight={delegateRight}
            revokeDelegation={revokeDelegation}
            onClose={() => setManagingRightId(null)}
          />
        );
      })()}
    </div>
  );
}

// ============================================================================
// Domain Section
// ============================================================================

function DomainSection({
  domain,
  rights,
  cycleOwnerName,
  canEdit,
  onManage,
}: {
  domain: CanonicalSessionType;
  rights: DecisionRight[];
  cycleOwnerName?: string;
  canEdit: boolean;
  onManage?: (right: DecisionRight) => void;
}) {
  return (
    <div className="border border-[var(--rule)] bg-[var(--bg-raised)] overflow-hidden">
      {/* Domain header */}
      <div className="flex items-center gap-3 px-4 py-3 bg-[var(--surface)] border-b border-[var(--rule)]">
        <div>
          <span className="text-sm font-semibold text-[var(--text-pri)]">
            {CANONICAL_SESSION_TYPE_LABELS[domain]}
          </span>
          <span className="ml-2 text-xs text-[var(--text-ter)]">
            {DOMAIN_DESCRIPTIONS[domain]}
          </span>
        </div>
      </div>

      {/* Rights rows */}
      <div className="divide-y divide-[var(--rule)]">
        {rights.length === 0 ? (
          <div className="px-4 py-3 text-sm text-[var(--text-ter)] italic">
            No rights configured for this domain.
          </div>
        ) : (
          rights.map(right => (
            <RightRow
              key={right.id}
              right={right}
              cycleOwnerName={cycleOwnerName}
              canEdit={canEdit}
              onManage={onManage}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ============================================================================
// Right Row
// ============================================================================

function RightRow({
  right,
  cycleOwnerName,
  canEdit,
  onManage,
}: {
  right: DecisionRight;
  cycleOwnerName?: string;
  canEdit: boolean;
  onManage?: (right: DecisionRight) => void;
}) {
  const constraints = DECISION_CLASS_EXECUTION_CONSTRAINTS[right.decisionClass];
  const isDelegatable = !!right.delegationConstraints;

  return (
    <div className="px-4 py-3">
      <div className="flex items-start gap-4 flex-wrap sm:flex-nowrap">

        {/* Decision class */}
        <div className="w-40 shrink-0">
          <span className="text-xs font-semibold text-[var(--text-sec)] uppercase tracking-wide">
            {CANONICAL_DECISION_CLASS_LABELS[right.decisionClass]}
          </span>
          <p className="text-xs text-[var(--text-ter)] mt-0.5 leading-tight">
            {CANONICAL_DECISION_CLASS_DESCRIPTIONS[right.decisionClass]}
          </p>
        </div>

        {/* Who decides */}
        <div className="flex-1 min-w-0">
          <OwnerDisplay
            right={right}
            cycleOwnerName={cycleOwnerName}
          />
        </div>

        {/* Execution modes */}
        <div className="shrink-0">
          <p className="text-xs text-[var(--text-ter)] mb-1.5">How they may decide</p>
          <ExecutionModeChips
            right={right}
            constraints={constraints}
          />
        </div>

        {/* Meta-assignment */}
        <div className="shrink-0 w-36">
          <p className="text-xs text-[var(--text-ter)] mb-1">Who may reassign</p>
          <MetaAssignmentDisplay right={right} cycleOwnerName={cycleOwnerName} />
        </div>

        {/* Edit affordance — opens this row's delegate/revoke panel */}
        {canEdit && onManage && (
          <button
            type="button"
            onClick={() => onManage(right)}
            title="Delegate or revoke this right"
            className="shrink-0 p-1.5 rounded hover:bg-[var(--hover-tint)] text-[var(--text-ter)] hover:text-[var(--text-sec)] transition-colors"
          >
            <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Delegation notice */}
      {isDelegatable && (
        <div className="mt-2 ml-44 flex items-center gap-1.5 text-xs text-[var(--text-ter)]">
          <ArrowRight className="h-3 w-3" />
          <span>
            Delegatable to:{' '}
            {right.delegationConstraints!.allowedRoleIds?.join(', ') ||
              right.delegationConstraints!.allowedPrincipalIds?.join(', ')}
            {right.delegationConstraints!.maxDepth > 0
              ? ` (max ${right.delegationConstraints!.maxDepth} re-delegation${right.delegationConstraints!.maxDepth > 1 ? 's' : ''})`
              : ''}
          </span>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Owner Display
// ============================================================================

function OwnerDisplay({
  right,
  cycleOwnerName,
}: {
  right: DecisionRight;
  cycleOwnerName?: string;
}) {
  const { owner } = right;

  let label = '';
  let sublabel = '';

  if (owner.bindingType === 'PRINCIPAL_IN_SCOPE') {
    label = cycleOwnerName ?? owner.principalId ?? 'Cycle Owner';
    sublabel = 'Named individual';
  } else if (owner.bindingType === 'ROLE_IN_SCOPE') {
    label = owner.roleId ?? 'Role';
    sublabel = 'Any user with this role';
  } else {
    label = cycleOwnerName ?? owner.principalId ?? 'Cycle Owner';
    sublabel = `${owner.roleId ?? 'Role'} + named individual`;
  }

  return (
    <div>
      <p className="text-xs text-[var(--text-ter)] mb-1">Who decides</p>
      <div className="flex items-center gap-2">
        <div className="h-6 w-6 rounded-full bg-[var(--slate-dim)] flex items-center justify-center shrink-0">
          <User className="h-3.5 w-3.5 text-[var(--text-ter)]" />
        </div>
        <div>
          <span className="text-sm font-medium text-[var(--text-pri)]">{label}</span>
          <span className="ml-1.5 text-xs text-[var(--text-ter)]">{sublabel}</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Execution Mode Chips
// ============================================================================

function ExecutionModeChips({
  right,
  constraints,
}: {
  right: DecisionRight;
  constraints: { allowedModes: DecisionExecutionMode[]; requiredModes?: DecisionExecutionMode[]; note?: string };
}) {
  const allModes: DecisionExecutionMode[] = ['solo', 'delegate', 'session'];

  return (
    <div className="flex items-center gap-1.5">
      {allModes.map(mode => {
        const isAllowed = right.allowedExecutionModes.includes(mode);
        const isForbidden = !constraints.allowedModes.includes(mode);
        const isRequired = constraints.requiredModes?.includes(mode);
        const config = MODE_CONFIG[mode];
        const Icon = config.icon;

        if (isForbidden) {
          return (
            <div
              key={mode}
              title={`${DECISION_EXECUTION_MODE_LABELS[mode]} — not valid for this decision class${constraints.note ? ': ' + constraints.note : ''}`}
              className="flex items-center gap-1 px-2 py-1 rounded border border-dashed border-[var(--rule)] bg-[var(--surface)] opacity-40 cursor-not-allowed"
            >
              <Icon className="h-3 w-3 text-[var(--text-ter)]" />
              <span className="text-xs text-[var(--text-ter)] capitalize">{mode}</span>
            </div>
          );
        }

        return (
          <div
            key={mode}
            title={
              DECISION_EXECUTION_MODE_LABELS[mode] +
              (isRequired ? ' (required for this class)' : '') +
              (!isAllowed ? ' — not permitted for this right' : '')
            }
            className={`flex items-center gap-1 px-2 py-1 rounded border transition-opacity ${
              isAllowed
                ? `${config.bgColor} ${config.borderColor} ${config.color}`
                : 'bg-[var(--surface)] border-[var(--rule)] text-[var(--text-ter)] opacity-40'
            } ${isRequired ? 'ring-1 ring-offset-1 ring-teal-400' : ''}`}
          >
            <Icon className="h-3 w-3" />
            <span className="text-xs capitalize font-medium">{mode}</span>
            {isRequired && (
              <span title="Required for convergence" className="inline-flex">
                <Lock aria-label="Required for convergence" className="h-2.5 w-2.5 ml-0.5 text-[var(--green)]" />
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// Meta-Assignment Display
// ============================================================================

function MetaAssignmentDisplay({
  right,
  cycleOwnerName,
}: {
  right: DecisionRight;
  cycleOwnerName?: string;
}) {
  const { metaAssignment } = right;

  let label = '';
  if (metaAssignment.bindingType === 'PRINCIPAL_IN_SCOPE') {
    label = cycleOwnerName ?? metaAssignment.principalId ?? 'Cycle Owner';
  } else if (metaAssignment.bindingType === 'ROLE_IN_SCOPE') {
    label = metaAssignment.roleId ?? 'Role';
  } else {
    label = cycleOwnerName ?? metaAssignment.principalId ?? 'Cycle Owner';
  }

  return (
    <div className="flex items-center gap-1.5">
      <ShieldCheck className="h-3.5 w-3.5 text-[var(--text-ter)] shrink-0" />
      <span className="text-xs text-[var(--text-sec)] truncate">{label}</span>
    </div>
  );
}

// ============================================================================
// Legend
// ============================================================================

function ExecutionModeLegend() {
  return (
    <div className="border border-[var(--rule)] bg-[var(--surface)] px-4 py-3">
      <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">
        Execution modes
      </p>
      <div className="flex flex-wrap gap-4">
        {(Object.entries(DECISION_EXECUTION_MODE_LABELS) as [DecisionExecutionMode, string][]).map(
          ([mode, label]) => {
            const config = MODE_CONFIG[mode];
            const Icon = config.icon;
            return (
              <div key={mode} className="flex items-center gap-2">
                <div
                  className={`flex items-center gap-1 px-2 py-1 rounded border ${config.bgColor} ${config.borderColor} ${config.color}`}
                >
                  <Icon className="h-3 w-3" />
                  <span className="text-xs font-medium capitalize">{mode}</span>
                </div>
                <span className="text-xs text-[var(--text-ter)]">{label}</span>
              </div>
            );
          }
        )}
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1 px-2 py-1 rounded border border-[var(--green)] bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)] ring-1 ring-offset-1 ring-teal-400">
            <Users className="h-3 w-3" />
            <span className="text-xs font-medium">session</span>
            <Lock className="h-2.5 w-2.5 text-[var(--green)]" />
          </div>
          <span className="text-xs text-[var(--text-ter)]">Required (convergence)</span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// Manage panel — delegate or revoke a single right (chevron affordance)
// ============================================================================

interface RightManagePanelProps {
  right: DecisionRight;
  cycleOwnerName?: string;
  participantOptions: { value: string; label: string }[];
  delegations: DelegatedDecisionRight[];
  callerId: string;
  delegateRight: (params: {
    rightId: string;
    delegatePrincipalId: string;
    allowedModes?: DecisionExecutionMode[];
    expiresAt?: Date;
    callerId: string;
  }) => Promise<{ success: boolean; error?: string; delegation?: DelegatedDecisionRight }>;
  revokeDelegation: (
    delegationId: string,
    callerId: string
  ) => Promise<{ success: boolean; error?: string }>;
  onClose: () => void;
}

function RightManagePanel({
  right,
  cycleOwnerName,
  participantOptions,
  delegations,
  callerId,
  delegateRight,
  revokeDelegation,
  onClose,
}: RightManagePanelProps) {
  const constraints = DECISION_CLASS_EXECUTION_CONSTRAINTS[right.decisionClass];
  const delegatableModes = right.allowedExecutionModes.filter(m => constraints.allowedModes.includes(m));

  const [delegatePrincipalId, setDelegatePrincipalId] = useState('');
  const [selectedModes, setSelectedModes] = useState<DecisionExecutionMode[]>(delegatableModes);
  const [expiresAt, setExpiresAt] = useState('');
  const [busy, setBusy] = useState(false);
  const [busyDelegationId, setBusyDelegationId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const addNotification = useUIStore(s => s.addNotification);

  const toggleMode = (mode: DecisionExecutionMode) => {
    setSelectedModes(prev => (prev.includes(mode) ? prev.filter(m => m !== mode) : [...prev, mode]));
  };

  const handleDelegate = async () => {
    if (!delegatePrincipalId || selectedModes.length === 0) return;
    setBusy(true);
    setError(null);
    // The end of the chosen day, in the person's own time zone (980).
    const endIso = expiresAt ? endOfDayIso(expiresAt) : null;
    const result = await delegateRight({
      rightId: right.id,
      delegatePrincipalId,
      allowedModes: selectedModes,
      expiresAt: endIso ? new Date(endIso) : undefined,
      callerId,
    });
    setBusy(false);
    if (!result.success) {
      setError(result.error ?? 'Could not delegate this right.');
      return;
    }
    const delegateName =
      participantOptions.find(o => o.value === delegatePrincipalId)?.label ?? 'The person you chose';
    addNotification({
      type: 'success',
      title: 'Right delegated',
      message: result.delegation && !result.delegation.accepted
        ? `${delegateName} has been told and needs to accept it. It shows under active delegations until then.`
        : `${delegateName} has been told and can now use it. It shows under active delegations — revoke it there.`,
      duration: 5000,
    });
    setDelegatePrincipalId('');
    setExpiresAt('');
  };

  const handleRevoke = async (delegationId: string) => {
    setBusyDelegationId(delegationId);
    setError(null);
    const result = await revokeDelegation(delegationId, callerId);
    setBusyDelegationId(null);
    if (!result.success) {
      setError(result.error ?? 'Could not revoke this delegation.');
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      size="md"
      title={`${CANONICAL_SESSION_TYPE_LABELS[right.domain]} — ${CANONICAL_DECISION_CLASS_LABELS[right.decisionClass]}`}
      description="Delegate this right to a cycle participant, or revoke an existing delegation."
    >
      <div className="space-y-5">
        {/* Current owner, for orientation */}
        <div>
          <OwnerDisplay right={right} cycleOwnerName={cycleOwnerName} />
        </div>

        {error && (
          <Callout tone="coral" title="That didn't go through">
            {error}
          </Callout>
        )}

        {/* Active delegations */}
        <div>
          <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide mb-2">
            Delegated to
          </p>
          {delegations.length === 0 ? (
            <p className="text-sm text-[var(--text-ter)]">No active delegations for this right.</p>
          ) : (
            <ul className="space-y-2">
              {delegations.map(d => (
                <li
                  key={d.id}
                  className="flex items-center justify-between gap-3 px-3 py-2 bg-[var(--surface)] border border-[var(--rule)]"
                >
                  <div className="min-w-0">
                    <p className="text-sm text-[var(--text-pri)] truncate">{d.delegatePrincipalId}</p>
                    <p className="text-xs text-[var(--text-ter)]">
                      {d.allowedExecutionModes.join(', ')}
                      {d.expiresAt
                        ? d.expiresAt.getTime() <= Date.now()
                          ? ` · expired ${d.expiresAt.toLocaleDateString()} — no longer held`
                          : ` · expires ${d.expiresAt.toLocaleDateString()}`
                        : ''}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => void handleRevoke(d.id)}
                    disabled={busyDelegationId === d.id}
                    className="shrink-0 inline-flex items-center gap-1 px-2 py-1 text-xs font-medium text-[var(--coral)] hover:bg-[var(--coral-dim)] border border-[var(--coral)] disabled:opacity-50"
                  >
                    <XIcon className="h-3 w-3" />
                    {busyDelegationId === d.id ? 'Revoking…' : 'Revoke'}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* New delegation */}
        {delegatableModes.length === 0 ? (
          <p className="text-sm text-[var(--text-ter)]">
            No execution mode is delegatable for this decision class.
          </p>
        ) : (
          <div className="space-y-3 pt-2 border-t border-[var(--rule)]">
            <p className="text-xs font-semibold text-[var(--text-ter)] uppercase tracking-wide">
              Delegate this right
            </p>
            <Field label="Principal" hint={participantOptions.length === 0 ? 'No active participants in this cycle yet.' : undefined}>
              <Select
                value={delegatePrincipalId}
                onChange={e => setDelegatePrincipalId(e.target.value)}
                placeholder="Choose a participant…"
                options={participantOptions}
                disabled={participantOptions.length === 0}
              />
            </Field>
            <div>
              <p className="text-xs text-[var(--text-sec)] mb-1.5">Allowed modes</p>
              <div className="flex flex-wrap gap-4">
                {delegatableModes.map(mode => (
                  <Checkbox
                    key={mode}
                    label={DECISION_EXECUTION_MODE_LABELS[mode]}
                    checked={selectedModes.includes(mode)}
                    onChange={() => toggleMode(mode)}
                  />
                ))}
              </div>
            </div>
            <Field label="Expires" hint="Optional — the delegation ends at the close of that day. Leave it blank and it stands until revoked.">
              <input
                type="date"
                value={expiresAt}
                onChange={e => setExpiresAt(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-[var(--rule)] bg-[var(--bg-raised)] text-[var(--text-pri)]"
              />
            </Field>
            <button
              type="button"
              onClick={() => void handleDelegate()}
              disabled={busy || !delegatePrincipalId || selectedModes.length === 0}
              className="px-3 py-1.5 text-sm font-medium text-white bg-[var(--slate)] hover:opacity-90 disabled:opacity-50"
            >
              {busy ? 'Delegating…' : 'Delegate'}
            </button>
          </div>
        )}
      </div>
    </Modal>
  );
}

export default DecisionRightsPanel;
